import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/auth.js';

interface RequireAuthProps {
  children: ReactNode;
  requireRole?: 'admin';
}

/**
 * Route gate. Splash while auth boot is in flight, redirect to /login with
 * a `?next=` hint when unauthenticated, redirect to / when authenticated
 * but lacking a required role.
 */
export function RequireAuth({ children, requireRole }: RequireAuthProps) {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (status === 'unknown' || status === 'loading') {
    return <GateSplash />;
  }

  if (status === 'unauthenticated' || !user) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (requireRole === 'admin' && user.role !== 'admin') {
    // P23 Batch F — non-admin authenticated users hitting an admin
    // route bounce to `/play` (the game) rather than `/` (landing).
    return <Navigate to="/play" replace />;
  }

  return <>{children}</>;
}

function GateSplash() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-paperDeep">
      <span className="stencil text-[11px] tracking-capsWide text-brass/60">
        Verifying credentials…
      </span>
    </div>
  );
}
